import debounce from "lodash/debounce"
import {$lara} from "@/utils/$larafetch"
import {tableModules} from "@/utils/tableModules"

export const useTableStore = () => {

    const name = ref(null)
    const route = ref(null)
    const defaultOrder = ref(null)
    const data = ref({}) // store previously fetched data
    const columns = ref(null)
    const filters = ref(null)
    const quickFilters = ref(null)
    const tools = ref(null)
    const idColumn = ref('id')
    const disablePreloadPages = ref(false)
    const enableSaveFilters = ref(false)
    const additionalData = ref(null) // store additional data from API response (e.g., sums, aggregations)

    const activeTool = ref(null)
    const loading = ref(false)
    const inverseSelection = ref(false)
    const selectedRows = ref([])
    const filterOptionCache = ref([])
    const activeRequestCounts = ref(new Map()) // Map to track active request counts per route

    const listing = ref({
        search: "",
        orderBy: "id",
        orderDirection: "asc",
        perPage: 10,
        perPageOptions: [10, 50, 100],
        columns: 6,
        page: 1,
        lastPage: 1,
        total: 0,
        custom: {},
        filter: [],
        links: []
    })

    const rows = computed(() => {
        if (listing.value.output === 'collectionAll') {
            return data.value[1] || []
        } else {
            return data.value[listing.value.page] || []
        }
    })

    const selectedCount = computed(() => {
        return inverseSelection.value ? listing.value.total - selectedRows.value.length : selectedRows.value.length
    })

    function resetSelection() {
        inverseSelection.value = false
        selectedRows.value = []
    }

    async function preloadPages() {
        const {$apiRoute} = useNuxtApp()
        for (let i = -1; i <= 2; i++) {
            const pageToLoad = listing.value.page + i
            if (pageToLoad < 1 || pageToLoad > listing.value.lastPage || data.value[pageToLoad]) continue
            try {
                const response = await $lara.get($apiRoute(route.value + '.index'), {
                    params: {...listing.value, page: pageToLoad}
                })
                data.value[pageToLoad] = response.data
            } catch (error) {
                console.error(error)
            }
        }
    }

    const preloadPagesDebounced = debounce(preloadPages, 1000, {maxWait: 5000})

    async function fetchData(fresh = false) {
        const {$apiRoute} = useNuxtApp()

        loading.value = true

        // Initialize the request count for this route if not already done
        if (!activeRequestCounts.value.has(route.value)) {
            activeRequestCounts.value.set(route.value, 0)
        }
        // Increment the active request count for the current route
        const currentRequestCount = activeRequestCounts.value.get(route.value) + 1
        activeRequestCounts.value.set(route.value, currentRequestCount)

        preloadPagesDebounced?.cancel()

        if (fresh || data.value[listing.value.page]) {
            data.value = {}
        }

        $lara.get($apiRoute(route.value + '.index'), {
            params: {...listing.value}
        }).then((response: Object) => {
            // Only process the response if it's the latest request for this route
            if (activeRequestCounts.value.get(route.value) !== currentRequestCount) return

            data.value[listing.value.page] = response.data
            listing.value.page = response.meta?.current_page ?? response.current_page
            listing.value.lastPage = response.meta?.last_page ?? response.last_page
            listing.value.total = response.meta?.total ?? response.total
            listing.value.links = response.meta?.links ?? response.links
            additionalData.value = response.additionalData ?? {}

            if (listing.value.page > listing.value.lastPage) {
                listing.value.page = listing.value.lastPage
            }

            if (!disablePreloadPages.value) {
                preloadPagesDebounced()
            }
        }).catch(error => {
            // console.error(error)
        }).finally(() => {
            loading.value = false
        })
    }

    async function postToolData(tool: string, data: Object = {}) {
        const {$apiRoute} = useNuxtApp()

        const customData = listing.value?.custom && (typeof listing.value.custom === 'object') ? listing.value.custom : {}

        return $lara.post($apiRoute(route.value + '.action'), {
            rawResponse: data.rawResponse ?? false,
            body: {
                ...listing.value,
                ...customData,
                selection: {
                    inverseSelection: inverseSelection.value ?? false,
                    selectedIds: selectedRows.value ?? []
                },
                tool,
                toolData: data
            }
        })
    }

    function initialize(module: string) {
        const moduleDefinition = tableModules[module]
        name.value = moduleDefinition.name
        route.value = moduleDefinition.route
        defaultOrder.value = moduleDefinition.defaultOrder ?? null
        idColumn.value = moduleDefinition.idColumn ?? 'id'
        disablePreloadPages.value = moduleDefinition.disablePreloadPages ?? false
        enableSaveFilters.value = moduleDefinition.enableSaveFilters ?? false
        columns.value = moduleDefinition.columns
        filters.value = moduleDefinition.filters ?? null
        quickFilters.value = moduleDefinition.quickFilters ?? null
        tools.value = moduleDefinition.tools ?? null

        if (defaultOrder.value) {
            listing.value.orderBy = defaultOrder.value.column
            listing.value.orderDirection = defaultOrder.value.direction
        }

        // Load filters from local storage
        const storedFilters = localStorage.getItem(`filters-${name.value}`)
        let validStoredFilters = []

        // check if stored filters are still valid
        if (storedFilters?.length > 0) {
            validStoredFilters = JSON.parse(storedFilters).filter((filter) => {
                const validFilter = filters?.value?.find((f) => f.name === filter.name)
                const validQuickFilter = quickFilters?.value?.find((f) => f.name === filter.name)
                return validFilter || validQuickFilter
            })
        }

        if (validStoredFilters?.length > 0) {
            listing.value.filter = validStoredFilters
        } else if (storedFilters === null) {
            // Apply default filters if no filters are stored
            quickFilters?.value?.forEach((filter) => {
                if (filter.default) {
                    listing.value.filter.push(filter)
                }
            })
            filters?.value?.forEach((filter) => {
                if (filter.default) {
                    const defaultFilter = {
                        column: filter.column,
                        name: filter.name,
                        operator: filter.default.operator,
                        value: filter.default.value
                    }
                    listing.value.filter.push(defaultFilter)
                }
            })
        }

        // Load orderBy and orderDirection from local storage
        const storedOrderBy = localStorage.getItem(`${name.value}-orderBy`)
        const storedOrderDirection = localStorage.getItem(`${name.value}-orderDirection`)

        // check if the stored order is still valid, it is passed to the database as a column name
        const validOrderBy = [
            'id',
            defaultOrder.value?.column,
            ...(columns.value ?? []).filter((column) => column.sortable).map((column) => column.sortBy || column.name)
        ]

        if (storedOrderBy && validOrderBy.includes(storedOrderBy)) {
            listing.value.orderBy = storedOrderBy
            if (storedOrderDirection) {
                listing.value.orderDirection = storedOrderDirection
            }
        }
    }

    // Watch filters and update local storage
    watch(() => listing.value.filter, (newFilters) => {
        if (newFilters.length > 0) {
            localStorage.setItem(`filters-${name.value}`, JSON.stringify(newFilters))
        } else {
            localStorage.setItem(`filters-${name.value}`, JSON.stringify([]))
        }
    }, {deep: true})

    // Watch orderBy and update local storage
    watch(() => listing.value.orderBy, (newOrderBy) => {
        localStorage.setItem(`${name.value}-orderBy`, newOrderBy)
    })

    // Watch orderDirection and update local storage
    watch(() => listing.value.orderDirection, (newOrderDirection) => {
        localStorage.setItem(`${name.value}-orderDirection`, newOrderDirection)
    })

    return {
        loading,
        columns,
        filters,
        quickFilters,
        tools,
        rows,
        listing,
        inverseSelection,
        selectedRows,
        selectedCount,
        activeTool,
        defaultOrder,
        idColumn,
        disablePreloadPages,
        enableSaveFilters,
        route,
        filterOptionCache,
        additionalData,
        initialize,
        fetchData,
        resetSelection,
        postToolData
    }
}